// 1. React
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";

// 2. Helpers
import { transformRoute, useTranslate } from "helpers";

// 3. Types
import { VehicleInvoiceSaleCardProps, VehicleInvoiceSaleRef } from "./types";
import { InvoiceOutgoingRow } from "services/api/fleet/vehicles/invoices/types";

// 4. API & Data
import {
  deleteFleetVehicleInvoiceAPI,
  getFleetVehicleInvoicesAPI,
} from "services/api/fleet/vehicles/invoices/api";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

// 5. Navigation
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";

// 6. UI Components
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// 7. Local Components
import VehicleInvoiceSaleEditCard from "../../../VehicleInvoiceSaleEditCard/VehicleInvoiceSaleEditCard";
import VehicleInvoiceSaleSearchResults from "../VehicleInvoiceSaleSearchResults/VehicleInvoiceSaleSearchResults";


const VehicleInvoiceSaleCard = forwardRef<VehicleInvoiceSaleRef, VehicleInvoiceSaleCardProps>((props, ref) => {
  const { fleetVehicleDetail } = props;

  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<InvoiceOutgoingRow>
  >(initialDataWithPagination);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [invoiceId, setInvoiceId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fleetVehicleDetail.id]);

  const fetchSearchResult = async (pagination: Pagination, vehicleId: string) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleInvoicesAPI(pagination, { vehicleId });

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setInvoiceId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setInvoiceId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  };

  const handleEditSuccess = useCallback(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
    setShowAddNewModal(false);
  }, [fleetVehicleDetail.id, searchResults.pagination]);


  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setInvoiceId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!invoiceId || !fleetVehicleDetail.id) return;

    try {
      const deleted = await deleteFleetVehicleInvoiceAPI(
        fleetVehicleDetail?.id,
        invoiceId,
        t
      );

      if (deleted && fleetVehicleDetail.id) {
        await fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setInvoiceId("");
    } catch (err) {
      console.error("Greška prilikom brisanja računa:", err);
    }
  }, [invoiceId, t, fleetVehicleDetail.id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setInvoiceId("");
    setOpenDialog(false);
  }, []);

  const openAddNew = () => {
    setInvoiceId("");
    setShowAddNewModal(true);
  };

  useImperativeHandle(ref, () => ({
    openAddNew,
    refresh: () => {
      if (fleetVehicleDetail.id)
        fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
    },
  }));

  const handleContactClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.preventDefault();
    event.stopPropagation();

    navigate(
      transformRoute(
        ContactRoutes.ContactDetailPageRouter,
        {
          id: id,
        }
      )
    );
  }

  if (!fleetVehicleDetail.id) return <></>;

  return (
    <>
      <div className="space-y-3 mt-1 ">
        <div className="flex justify-between items-center">
          <div className="px-3">
          </div>
          <div className="flex items-center gap-2 px-3 my-3 ">
              <Button
                size="small"
                variant="primary"
                children={t("admin.fleet.vehicle.invoice.sale.search.add")}
                onClick={handleAddNew}
                startIcon="/media/icons/plus.svg"
              />
          </div>
        </div>
        <div className="m-[-1px]">
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.fleet.vehicle.invoice.sale.search.noresults")} />
          ) : (
            <VehicleInvoiceSaleSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onContactClick={handleContactClick}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>
      <VehicleInvoiceSaleEditCard
        show={showAddNewModal}
        vehicleId={fleetVehicleDetail.id}
        invoiceId={invoiceId}
        onSuccess={handleEditSuccess}
        onClose={handleEditSuccess}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.invoice.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.invoice.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.invoice.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.invoice.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
});

export default memo(VehicleInvoiceSaleCard);
